// src/components/MediaTile.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import SeasonCard from './SeasonCard';
import debounce from 'lodash/debounce';
import { getExternalUrl } from '../utils/externalUrl';
import { getCurrentSeason, getSeasonTitle } from '../utils/currentSeason';
import '../styles/MediaTile.css';

const MediaTile = ({ media, statusOptions, apiBaseUrl, onUpdate, mediaType }) => {
  const [expanded, setExpanded] = useState(false);
  const { userRoles } = useAuth();
  const hasManageRole = userRoles.includes('manage-media');

  useEffect(() => {
    const images = [media.horizontal_image, media.vertical_image].filter(Boolean);
    images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [media.horizontal_image, media.vertical_image]);

  const toggleExpand = () => {
    console.log('Toggling expand for:', media.title, 'New state:', !expanded);
    setExpanded(!expanded);
  };

  const handleTileClick = (e) => {
    // Ctrl+click or middle-click opens details in new tab
    if (e.ctrlKey || e.button === 1) {
      e.preventDefault();
      e.stopPropagation();
      window.open(`/media-item/${media.id}`, '_blank', 'noopener,noreferrer');
    } else if (e.button === 0) {
      // Normal left-click toggles expansion
      toggleExpand();
    }
  };

  const handleViewPageClick = (e) => {
    e.stopPropagation(); // Prevent tile expansion
    // Ctrl+click or middle-click opens in new tab
    if (e.ctrlKey || e.button === 1) {
      e.preventDefault();
      window.open(`/media-item/${media.id}`, '_blank', 'noopener,noreferrer');
    } else {
      // Normal click navigates in same tab
      window.location.href = `/media-item/${media.id}`;
    }
  };

  // Keep a ref to the latest handler so the debounce timer (created once) always
  // acts on current props/state without needing to be recreated on every render.
  const handleQuickEditRef = useRef();
  handleQuickEditRef.current = (field, value, seasonId = null) => {
    const basePath = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
    const url = seasonId
      ? `${basePath}/update_season_setting/${seasonId}`
      : `${basePath}/update_quick_setting/${media.id}`;
    console.log('Sending request to:', url, { field, value });
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ field, value, media_type: media.type }),
    })
      .then(res => {
        console.log('Response status:', res.status);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => {
        console.log('Response data:', data);
        if (data.success) {
          if (seasonId) {
            onUpdate?.({ ...media, seasons: media.seasons.map(s => s.id === seasonId ? data.season : s) });
          } else {
            onUpdate?.({ ...media, ...data.media });
          }
        } else {
          console.error('Update failed:', data);
          alert('Failed to save changes: ' + (data.message || 'Unknown error'));
        }
      })
      .catch(err => {
        console.error('Fetch error:', err);
        alert('Error saving changes: ' + err.message);
      });
  };

  const debouncedQuickEdit = useRef(
    debounce((...args) => handleQuickEditRef.current(...args), 500)
  ).current;

  const handleQuickEdit = (field, value, seasonId = null) => {
    debouncedQuickEdit(field, value, seasonId);
  };

  const achievementPercentage = media.type === 'SteamGame' && media.total_achievements
    ? (media.achievements_unlocked / media.total_achievements * 100).toFixed(2)
    : 0;

  const renderTags = () => {
    const tags = [];
    if (media.is_favorite) {
      tags.push(
        <span className="tag" key="completed">
          <img src="/static/icons/star-solid.svg" className="icon" alt="Completed" />
        </span>
      );
    }
    if (media.explicit) {
      tags.push(
        <span className="tag" key="completed">
          <img src="/static/icons/triangle-exclamation-solid.svg" alt="Explicit" className="field-icon icon" /> Explicit
        </span>
      );
    }
    if (media.type === 'SteamGame') {
      if (media.status === 'Completed') {
        tags.push(
          <span className="tag" key="completed">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Completed" />
            Completed
          </span>
        );
      }
      tags.push(
        <span className="tag" key="playtime">
          {media.playtime ? (
            <>
              <img src="/static/icons/clock-solid.svg" className="icon" alt="Playtime" />
              Playtime: {(media.playtime / 60).toFixed(1)}h
            </>
          ) : 'No recorded playtime'}
        </span>
      );
      if (media.playtime_windows && media.playtime_windows > 0) {
        tags.push(
          <span className="tag" key="playtime_windows">
            <img src="/static/icons/windows.svg" className="icon" alt="Windows Playtime" />
            {(media.playtime_windows / 60).toFixed(1)}h
          </span>
        );
      }
      if (media.playtime_deck && media.playtime_deck > 0) {
        tags.push(
          <span className="tag" key="playtime_deck">
            <img src="/static/icons/steamdeck.svg" className="icon" alt="Steam Deck Playtime" />
            {(media.playtime_deck / 60).toFixed(1)}h
          </span>
        );
      }
      if (media.total_achievements > 0) {
        tags.push(
          <div className="achievement-container" key="achievements">
            <div className="progress-bar">
              <div className="progress" style={{ width: `${achievementPercentage}%` }}></div>
            </div>
            <span className="achievement-text">{media.achievements_unlocked} / {media.total_achievements}</span>
          </div>
        );
        if (media.achievements_unlocked >= media.total_achievements) {
          tags.push(
            <span className="tag" key="perfect_game">
              <img src="/static/icons/medal-solid.svg" className="icon" alt="Perfect Game" />
              Perfect Game
            </span>
          );
        }
      }
    } else if (media.type === 'Anime' || media.type === 'AnimeMovie' || media.type === 'AnimeSerie') {
      const hasMultipleSeasons = media.seasons?.length > 1;
      const current = getCurrentSeason(media);
      const status = (current.status || 'Unknown').toLowerCase();
      if (mediaType === 'ShowsAndMovies') {
        const isMovie = media.media_type === 'movie';
        tags.push(
          <span className="tag" key="watch_type">
            <img src={isMovie ? '/static/icons/film-solid.svg' : '/static/icons/tv-solid.svg'} className="icon" alt={isMovie ? 'Movie' : 'Series'} />
            {isMovie ? 'Movie' : 'Series'}
          </span>
        );
      }
      if (status === 'plan to watch') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/list-solid.svg" className="icon" alt="Plan to Watch" />
            Plan to Watch{hasMultipleSeasons ? `: ${getSeasonTitle(current)}` : ''}
          </span>
        );
      } else if (status === 'watching') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/eye-solid.svg" className="icon" alt="Watching" />
            Watching: {hasMultipleSeasons ? `${getSeasonTitle(current)} – ` : ''}Ep. {current.current_episode || 0}{current.episode_count ? ` / ${current.episode_count}` : ''}
          </span>
        );
      } else if (status === 'on hold') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/eye-slash-solid.svg" className="icon" alt="On Hold" />
            On Hold: {hasMultipleSeasons ? `${getSeasonTitle(current)} – ` : ''}Ep. {current.current_episode || 0}{current.episode_count ? ` / ${current.episode_count}` : ''}
          </span>
        );
      } else if (status === 'forgotten') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/ghost-solid.svg" className="icon" alt="Forgotten" />
            Forgotten: {hasMultipleSeasons ? `${getSeasonTitle(current)} – ` : ''}Ep. {current.current_episode || 0}{current.episode_count ? ` / ${current.episode_count}` : ''}
          </span>
        );
      } else if (status === 'dropped') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/circle-xmark-solid.svg" className="icon" alt="Dropped" />
            Dropped: {hasMultipleSeasons ? `${getSeasonTitle(current)} – ` : ''}Ep. {current.current_episode || 0}{current.episode_count ? ` / ${current.episode_count}` : ''}
          </span>
        );
      } else if (status === 'finished') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Finished" />
            Finished
          </span>
        );
      } else {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/clipboard-question-solid.svg" className="icon" alt="Unknown" />
            Unknown status
          </span>
        );
      }
      if (hasMultipleSeasons) {
        tags.push(
          <span className="tag" key="seasons">
            <img src="/static/icons/clapperboard-solid.svg" className="icon" alt="Seasons" />
            {media.seasons.length} Seasons
          </span>
        );
      }
      if (media.mal_score) {
        tags.push(
          <span className="tag" key="mal_score">
            <img src="/static/icons/myanimelist.svg" className="icon" alt="MAL Score" />
            Score: {media.mal_score}
          </span>
        );
      }
    } else if (media.type === 'Manga') {
      if (media.ownership_status === 'Wishlisted') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/star-solid.svg" className="icon" alt="Wishlisted" />
            Wishlisted
          </span>
        );
      } else if (media.ownership_status === 'Collecting') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/book-solid.svg" className="icon" alt="Collecting" />
            Collecting {media.owned_volume_count ? `${media.owned_volume_count}` : ''}{media.volume_count ? `/ ${media.volume_count}` : ''}
          </span>
        );
      } else if (media.ownership_status === 'Owned') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/check-double-solid.svg" className="icon" alt="Owned" />
            Full Series Owned
          </span>
        );
      }
      if (media.status === 'Finished') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Finished" />
            Finished
          </span>
        );
      } else if (media.status === 'Reading') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/book-open-solid.svg" className="icon" alt="Reading" />
            Reading: {media.current_volume !== null ? `vol. ${media.current_volume}, ` : ''}Ch. {media.current_chapter || 0}
          </span>
        );
      } else if (media.status === 'Dropped') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/circle-xmark-solid.svg" className="icon" alt="Dropped" />
            Dropped: {media.current_volume !== null ? `vol. ${media.current_volume}, ` : ''}Ch. {media.current_chapter || 0}
          </span>
        );
      } else if (media.status === 'On Hold') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/book-solid.svg" className="icon" alt="On Hold" />
            On Hold: {media.current_volume !== null ? `vol. ${media.current_volume}, ` : ''}Ch. {media.current_chapter || 0}
          </span>
        );
      } else if (media.status === 'Plan to Read') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/list-solid.svg" className="icon" alt="Plan to Read" />
            Plan to Read
          </span>
        );
      }
      if (media.mal_score) {
        tags.push(
          <span className="tag" key="mal_score">
            <img src="/static/icons/myanimelist.svg" className="icon" alt="MAL Score" />
            Score: {media.mal_score}
          </span>
        );
      }
      if (media.condition) {
        const conditionMap = {
          Excellent: { icon: '/static/icons/circle-check-solid.svg', text: 'Excellent Condition' },
          Good: { icon: '/static/icons/check-solid.svg', text: 'Good Condition' },
          Okay: { icon: '/static/icons/circle-solid.svg', text: 'Okay Condition' },
          Bad: { icon: '/static/icons/circle-xmark-solid.svg', text: 'Bad Condition' },
          Poor: { icon: '/static/icons/xmark-solid.svg', text: 'Poor Condition' },
        };
        const { icon, text } = conditionMap[media.condition] || { icon: '/static/icons/circle-solid.svg', text: media.condition };
        tags.push(
          <span className="tag" key="condition">
            <img src={icon} className="icon" alt={text} />
            {text}
          </span>
        );
      }
    } else if (['PhysicalGame', 'PSPGame', 'NDSGame', 'GameboyGame', 'SwitchGame', 'PS3Game', 'VitaGame'].includes(media.type)) {
      if (media.platform && media.type === 'PhysicalGame' && mediaType === 'PhysicalGame') {
        if (media.platform === 'psp') {
          tags.push(
            <span className="tag" key="platform">
              <img src="/static/icons/playstation.svg" className="icon" alt="Platform" />
              PSP
            </span>
          );
        } else if (media.platform === 'vita') {
          tags.push(
            <span className="tag" key="platform">
              <img src="/static/icons/playstation.svg" className="icon" alt="Platform" />
              PSVita
            </span>
          );
        } else if (media.platform === 'nds') {
          tags.push(
            <span className="tag" key="platform">
              <img src="/static/icons/nintendo-ds.svg" className="icon" alt="Platform" />
              NDS
            </span>
          );
        } else if (media.platform === 'switch') {
          tags.push(
            <span className="tag" key="platform">
              <img src="/static/icons/nintendoswitch.svg" className="icon" alt="Platform" />
              Nintendo Switch
            </span>
          );
        } else{
          tags.push(
            <span className="tag" key="platform">
              <img src="/static/icons/gamepad-solid.svg" className="icon" alt="Platform" />
              {media.platform.charAt(0).toUpperCase() + media.platform.slice(1)}
            </span>
          );
        }
      }
      if (media.status === 'Completed') {
        tags.push(
          <span className="tag" key="completed">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Completed" />
            Completed
          </span>
        );
      }
      if (media.ownership_status === 'Owned') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/check-double-solid.svg" className="icon" alt="Owned" />
            Owned
          </span>
        );
      } else if (media.ownership_status === 'Wishlisted') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/star-solid.svg" className="icon" alt="Wishlisted" />
            Wishlisted
          </span>
        );
      }
      if (media.condition) {
        const conditionMap = {
          Excellent: { icon: '/static/icons/circle-check-solid.svg', text: 'Excellent Condition' },
          Good: { icon: '/static/icons/check-solid.svg', text: 'Good Condition' },
          Okay: { icon: '/static/icons/circle-solid.svg', text: 'Okay Condition' },
          Bad: { icon: '/static/icons/circle-xmark-solid.svg', text: 'Bad Condition' },
          Poor: { icon: '/static/icons/xmark-solid.svg', text: 'Poor Condition' },
        };
        const { icon, text } = conditionMap[media.condition] || { icon: '/static/icons/circle-solid.svg', text: media.condition };
        tags.push(
          <span className="tag" key="condition">
            <img src={icon} className="icon" alt={text} />
            {text}
          </span>
        );
      }
    } else if (['Music', 'Vinyl', 'CD'].includes(media.type)) {
      if (media.type === 'Music' && mediaType === 'Music' && media.format) {
        if (media.format === 'vinyl') {
          tags.push(
            <span className="tag" key="format">
              <img src="/static/icons/record-vinyl-solid.svg" className="icon" alt="Vinyl" />
              Vinyl
            </span>
          );
        } else if (media.format === 'cd') {
          tags.push(
            <span className="tag" key="format">
              <img src="/static/icons/compact-disc-solid.svg" className="icon" alt="CD" />
              CD
            </span>
          );
        }
      }
      if (media.ownership_status === 'Owned') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/check-double-solid.svg" className="icon" alt="Owned" />
            Owned
          </span>
        );
      } else if (media.ownership_status === 'Wishlisted') {
        tags.push(
          <span className="tag" key="ownership">
            <img src="/static/icons/star-solid.svg" className="icon" alt="Wishlisted" />
            Wishlisted
          </span>
        );
      }
      if (media.condition) {
        const conditionMap = {
          Excellent: { icon: '/static/icons/circle-check-solid.svg', text: 'Excellent Condition' },
          Good: { icon: '/static/icons/check-solid.svg', text: 'Good Condition' },
          Okay: { icon: '/static/icons/circle-solid.svg', text: 'Okay Condition' },
          Bad: { icon: '/static/icons/circle-xmark-solid.svg', text: 'Bad Condition' },
          Poor: { icon: '/static/icons/xmark-solid.svg', text: 'Poor Condition' },
        };
        const { icon, text } = conditionMap[media.condition] || { icon: '/static/icons/circle-solid.svg', text: media.condition };
        tags.push(
          <span className="tag" key="condition">
            <img src={icon} className="icon" alt={text} />
            {text}
          </span>
        );
      }
    } else if (media.type === 'ScreenMedia') {
      const isTV = media.media_format?.toLowerCase() === 'tv';
      const hasMultipleSeasons = media.seasons?.length > 1;
      const current = getCurrentSeason(media);
      const status = (current.status || media.status || 'Unknown').toLowerCase();
      if (mediaType === 'ShowsAndMovies') {
        tags.push(
          <span className="tag" key="watch_type">
            <img src={isTV ? '/static/icons/tv-solid.svg' : '/static/icons/film-solid.svg'} className="icon" alt={isTV ? 'Series' : 'Movie'} />
            {isTV ? 'Series' : 'Movie'}
          </span>
        );
      }
      if (status === 'plan to watch') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/list-solid.svg" className="icon" alt="Plan to Watch" />
            Plan to Watch{hasMultipleSeasons ? `: ${getSeasonTitle(current)}` : ''}
          </span>
        );
      } else if (status === 'watching') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/eye-solid.svg" className="icon" alt="Watching" />
            Watching{isTV ? `: ${hasMultipleSeasons ? `${getSeasonTitle(current)} – ` : ''}Ep. ${current.current_episode ?? 0}${current.episode_count ? ` / ${current.episode_count}` : ''}` : ''}
          </span>
        );
      } else if (status === 'finished') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/trophy-solid.svg" className="icon" alt="Finished" />
            Finished
          </span>
        );
      } else if (status === 'on hold') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/eye-slash-solid.svg" className="icon" alt="On Hold" />
            On Hold{isTV && hasMultipleSeasons ? `: ${getSeasonTitle(current)}` : ''}
          </span>
        );
      } else if (status === 'dropped') {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/circle-xmark-solid.svg" className="icon" alt="Dropped" />
            Dropped{isTV && hasMultipleSeasons ? `: ${getSeasonTitle(current)}` : ''}
          </span>
        );
      } else {
        tags.push(
          <span className="tag" key="status">
            <img src="/static/icons/clipboard-question-solid.svg" className="icon" alt="Unknown" />
            Unknown status
          </span>
        );
      }

      if (hasMultipleSeasons) {
        tags.push(
          <span className="tag" key="seasons">
            <img src="/static/icons/clapperboard-solid.svg" className="icon" alt="Seasons" />
            {media.seasons.length} Seasons
          </span>
        );
      }
    
      if (media.tmdb_score) {
        tags.push(
          <span className="tag" key="tmdb_score">
            <img src="/static/icons/star-half-stroke-solid.svg" className="icon" alt="TMDB Score" />
            TMDB: {media.tmdb_score}
          </span>
        );
      }
    }



    return tags.length > 0 ? tags : null;
  };

  const renderExpandedContent = () => (
    <div className="expanded-content">
      <img src={media.vertical_image || media.horizontal_image} alt={media.title} className="poster" />
      <div className="expanded-details">
        {/* <hr style={{color: 'ffffff'}}></hr> */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
          <button
              className="view-page-btn btn"
              onClick={handleViewPageClick}
            >
              <img src="/static/icons/magnifying-glass-solid.svg" className="icon" alt="Media Link" style={{ height: '16px', width: '16px', marginRight: '5px' }} />

              View Media Page
            </button>
            <a
            
              href={
                media.type?.toLowerCase() === "screenmedia"
                  ? getExternalUrl(media.media_format?.toLowerCase(), media.external_id)
                  : getExternalUrl(media.type, media.external_id, media.source)
              }
              className="external-btn btn"
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ marginLeft: '10px' }}
            >
              <img src="/static/icons/link-solid.svg" className="icon" alt="External Link" style={{ height: '16px', width: '16px', marginRight: '5px' }} />
              View External
            </a>
        </div>

        <p className="description">{media.description || 'No description available.'}</p>

        {media.type === 'SteamGame' && (
          <>
            <p><strong>Developer:</strong> {media.developer || 'Unknown'}</p>
            <p><strong>Publisher:</strong> {media.publisher || 'Unknown'}</p>
            <p><strong>Genre:</strong> {media.genre || 'Unknown'}</p>
            <p><strong>Release Date:</strong> {media.release_date || 'Unknown'}</p>
          </>
        )}
        {(media.type === 'Anime' || media.type === 'AnimeMovie' || media.type === 'AnimeSerie') && (
          <>
            <p><strong>Studios:</strong> {media.studios || 'Unknown'}</p>
            <p><strong>Genres:</strong> {media.genre || 'Unknown'}</p>
            <p><strong>Episode Count:</strong> {media.episode_count || 'Unknown'}</p>
            <p><strong>MAL Score:</strong> {media.mal_score || 'N/A'}</p>
          </>
        )}
        {media.type === 'Manga' && (
          <>
            <p><strong>Genres:</strong> {media.genre || 'Unknown'}</p>
            <p><strong>Volume Count:</strong> {media.volume_count || 'Unknown'}</p>
            <p><strong>MAL Score:</strong> {media.mal_score || 'N/A'}</p>
          </>
        )}
        {hasManageRole ? (
          <>
            {((media.type !== 'Anime' && media.type !== 'AnimeMovie' && media.type !== 'AnimeSerie') || (media.seasons?.length ?? 0) <= 1) && (
              <p>
                <strong>Status:</strong>
                <select
                  className="quick-edit select-field"
                  defaultValue={media.status || 'Plan to Watch'}
                  onClick={e => e.stopPropagation()}
                  onChange={e => handleQuickEdit('status', e.target.value)}
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </p>
            )}
            {(media.type === 'Anime' || media.type === 'AnimeMovie' || media.type === 'AnimeSerie') && (media.seasons?.length ?? 0) <= 1 && (
              <p className="episode-field" style={{ display: media.status === 'Finished' ? 'none' : 'block' }}>
                <strong>Current Episode:</strong>
                <input
                  type="number"
                  className="quick-edit input-field"
                  style={{ width: '50px', minWidth: '50px' }}
                  min="0"
                  max={media.episode_count || undefined}
                  defaultValue={media.current_episode || 0}
                  onClick={e => e.stopPropagation()}
                  onChange={e => handleQuickEdit('current_episode', e.target.value)}
                />
                {media.episode_count && ` / ${media.episode_count}`}
              </p>
            )}
            {media.type === 'Manga' && (
              <>
                <p>
                  <strong>Current Chapter:</strong>
                  <input
                    type="number"
                    className="quick-edit input-field"
                    min="0"
                    defaultValue={media.current_chapter || 0}
                    onClick={e => e.stopPropagation()}
                    onChange={e => handleQuickEdit('current_chapter', e.target.value)}
                  />
                </p>
                <p>
                  <strong>Current Volume:</strong>
                  <input
                    type="number"
                    className="quick-edit input-field"
                    min="0"
                    defaultValue={media.current_volume || 0}
                    onClick={e => e.stopPropagation()}
                    onChange={e => handleQuickEdit('current_volume', e.target.value)}
                  />
                </p>
                <p>
                  <strong>Ownership:</strong>
                  <select
                    className="quick-edit select-field"
                    defaultValue={media.ownership_status || 'Not Owned'}
                    onClick={e => e.stopPropagation()}
                    onChange={e => handleQuickEdit('ownership_status', e.target.value)}
                  >
                    {['Not Owned', 'Wishlisted', 'Collecting', 'Owned'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </p>
                {media.ownership_status === 'Collecting' && (
                  <p className="ownership-volume-field">
                    <strong>Volumes Owned:</strong>
                    <input
                      type="number"
                      className="quick-edit input-field"
                      min="0"
                      defaultValue={media.owned_volume_count || 0}
                      onClick={e => e.stopPropagation()}
                      onChange={e => handleQuickEdit('owned_volume_count', e.target.value)}
                    />
                    {media.volume_count && ` / ${media.volume_count}`}
                  </p>
                )}
                <p>
                  <strong>Condition:</strong>
                  <select
                    className="quick-edit select-field"
                    defaultValue={media.condition || ''}
                    onClick={e => e.stopPropagation()}
                    onChange={e => handleQuickEdit('condition', e.target.value)}
                  >
                    <option value="">Select Condition</option>
                    {['Excellent', 'Good', 'Okay', 'Bad', 'Poor'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </p>
              </>
            )}
            {['PhysicalGame', 'PSPGame', 'NDSGame', 'GameboyGame', 'SwitchGame', 'PS3Game', 'VitaGame', 'Music', 'Vinyl', 'CD'].includes(media.type) && (
              <>
                <p>
                  <strong>Ownership:</strong>
                  <select
                    className="quick-edit select-field"
                    defaultValue={media.ownership_status || 'Not Owned'}
                    onClick={e => e.stopPropagation()}
                    onChange={e => handleQuickEdit('ownership_status', e.target.value)}
                  >
                    {['Not Owned', 'Wishlisted', 'Owned'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </p>
                <p>
                  <strong>Condition:</strong>
                  <select
                    className="quick-edit select-field"
                    defaultValue={media.condition || ''}
                    onClick={e => e.stopPropagation()}
                    onChange={e => handleQuickEdit('condition', e.target.value)}
                  >
                    <option value="">Select Condition</option>
                    {['Excellent', 'Good', 'Okay', 'Bad', 'Poor'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </p>
              </>
            )}
            <p>
              <strong>Score:</strong>
              <input
                type="number"
                className="quick-edit input-field"
                min="0"
                max="10"
                step="0.1"
                defaultValue={media.score || ''}
                onClick={e => e.stopPropagation()}
                onChange={e => handleQuickEdit('score', e.target.value)}
              />
            </p>
            <p>
              <strong>Privacy:</strong>
              <select
                className="quick-edit select-field"
                defaultValue={media.private || 0}
                onClick={e => e.stopPropagation()}
                onChange={e => handleQuickEdit('private', e.target.value)}
              >
                <option value="0">Public</option>
                <option value="1">Logged in users only</option>
                <option value="2">Allowed users only</option>
                <option value="3">Only visible to me</option>
                <option value="4">Hidden</option>
              </select>
            </p>         
          </>
        ) : (
          <>
            <hr />
            {((media.type !== 'Anime' && media.type !== 'AnimeMovie' && media.type !== 'AnimeSerie') || (media.seasons?.length ?? 0) <= 1) && (
              <p><strong>Current Status:</strong> {media.status || 'Unknown'}</p>
            )}
            {(media.type === 'Anime' || media.type === 'AnimeMovie' || media.type === 'AnimeSerie') && (media.seasons?.length ?? 0) <= 1 && (
              <p><strong>Current Episode:</strong> {media.current_episode || '0'} {media.episode_count ? `/ ${media.episode_count}` : ''}</p>
            )}
            {media.type === 'Manga' && (
              <>
                <p><strong>Current Chapter:</strong> {media.current_chapter || '0'}</p>
                <p><strong>Current Volume:</strong> {media.current_volume || '0'}{media.volume_count ? `/ ${media.volume_count}` : ''}</p>
                {media.ownership_status === 'Collecting' && (
                  <p><strong>Owned Volumes:</strong> {media.owned_volume_count || '0'} out of {media.volume_count || 'Unknown'}</p>
                )}
                {media.condition && (
                  <p><strong>Condition:</strong> {media.condition} Condition</p>
                )}
              </>
            )}
            {['PhysicalGame', 'PSPGame', 'NDSGame', 'GameboyGame', 'SwitchGame', 'PS3Game', 'VitaGame', 'Music', 'Vinyl', 'CD'].includes(media.type) && (
              <>
                <p><strong>Ownership:</strong> {media.ownership_status || 'Not Owned'}</p>
                {media.condition && (
                  <p><strong>Condition:</strong> {media.condition} Condition</p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderSeasons = () => {
    console.log('Checking seasons for:', media.title, 'Expanded:', expanded, 'Seasons:', media.seasons);
    if ((media.type === 'ScreenMedia' || media.type === 'Anime' || media.type === 'AnimeMovie' || media.type === 'AnimeSerie') && Array.isArray(media.seasons) && media.seasons.length > 1 && expanded) {
      console.log('Rendering season container for:', media.title);
      return (
        <div className="season-container" data-media-id={media.id}>
          <h2>Seasons ({media.seasons.length})</h2>
          <div className="season-grid">
            {media.seasons.map(season => {
              console.log('Mapping season:', season.id, season.title_en);
              return (
                <SeasonCard
                  key={season.id}
                  mediaItem={media}
                  season={season}
                  // seasonCount={0}
                  statusOptions={statusOptions}
                  apiBaseUrl={apiBaseUrl}
                  hasManageRole={hasManageRole}
                  onQuickEdit={handleQuickEdit}
                />
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className={`media-tile ${expanded ? 'expanded' : ''}`}
      onClick={handleTileClick}
      // onMouseDown={handleTileClick}
      data-title={media.title}
      data-media-id={media.id}
      data-score={media.score || 0}
      data-status={media.status}
      data-playtime={media.type === 'SteamGame' ? media.playtime || 0 : undefined}
      data-achievements={achievementPercentage}
      data-current-episode={(media.type === 'Anime' || media.type === 'AnimeMovie' || media.type === 'AnimeSerie') ? media.current_episode || 0 : undefined}
      data-media-type={(media.type === 'Anime' || media.type === 'AnimeMovie' || media.type === 'AnimeSerie') ? media.media_type || 'unknown' : undefined}
      data-ownership-status={['Manga', 'PhysicalGame', 'PSPGame', 'NDSGame', 'GameboyGame', 'SwitchGame', 'PS3Game', 'VitaGame', 'Music', 'Vinyl', 'CD'].includes(media.type) ? media.ownership_status || 'Not Owned' : undefined}
      data-condition={['Manga', 'PhysicalGame', 'PSPGame', 'NDSGame', 'GameboyGame', 'SwitchGame', 'PS3Game', 'VitaGame', 'Music', 'Vinyl', 'CD'].includes(media.type) ? media.condition || undefined : undefined}
      data-is-animated={media.is_animated === true ? 'true' : 'false'}
    >
      <div className="blurred-bg" style={{ backgroundImage: `url('${media.horizontal_image}')` }}></div>
      <div className="thumbnail-wrapper">
        <img src={media.horizontal_image || media.vertical_image} alt={media.title} className="thumbnail" />
        <img src={media.vertical_image || media.horizontal_image} alt={media.title} className="thumbnail_poster" />
      </div>
      <div className="info">
        <div className="title-score">
          <h3 className="media-title">{media.title}</h3>
          <span className="score">
            {typeof media.score === 'number' 
              ? (media.score === 10 ? '10' : media.score.toFixed(1))
              : '--'}
          </span>
        </div>
        <div className="tag-container">{renderTags()}</div>
      </div>
      {expanded && renderExpandedContent()}
      {renderSeasons()}
    </div>
  );
};

export default MediaTile;